export type ChatMessageSegment =
  | { type: 'text'; content: string }
  | { type: 'bold'; content: string }
  | { type: 'link'; content: string; href: string };

const SITE_ORIGIN = 'https://workflow.estate';

/** Strip trailing punctuation often glued to URLs in prose. */
export function normalizeChatHref(raw: string): string | null {
  const trimmed = raw.trim().replace(/[.,;:!?)]+$/g, '');
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return url.href;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (/^workflow\.estate\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  if (trimmed.startsWith('/')) {
    return `${SITE_ORIGIN}${trimmed}`;
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return `mailto:${trimmed}`;
  }

  return null;
}

const TOKEN_PATTERN =
  /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|https?:\/\/[^\s<>"']+|workflow\.estate\/[^\s<>"')]+|\/(?:register|plans|demo|support|login|contractor)(?:\/[^\s<>"')]*)?|[a-z0-9._%+-]+@workflow\.estate)/gi;

function segmentFromToken(token: string): ChatMessageSegment {
  const markdownLink = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (markdownLink) {
    const href = normalizeChatHref(markdownLink[2]);
    if (href) {
      return { type: 'link', content: markdownLink[1], href };
    }
    return { type: 'text', content: token };
  }

  const bold = token.match(/^\*\*([^*]+)\*\*$/);
  if (bold) {
    const href = normalizeChatHref(bold[1]);
    if (href) {
      return { type: 'link', content: bold[1], href };
    }
    return { type: 'bold', content: bold[1] };
  }

  const href = normalizeChatHref(token);
  if (href) {
    return { type: 'link', content: token, href };
  }

  return { type: 'text', content: token };
}

/** Split assistant/chat plain text into text, bold, and link segments. */
export function parseChatMessageSegments(text: string): ChatMessageSegment[] {
  if (!text) return [];

  const segments: ChatMessageSegment[] = [];
  let lastIndex = 0;
  const pattern = new RegExp(TOKEN_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    segments.push(segmentFromToken(match[0]));
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: 'text', content: text }];
}
